import { useEffect, useMemo, useState } from 'react'
import { DatePicker, Switch, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { timetableBeta } from '@/api'
import CheckboxViolet from '@UI/New/ChecboxViolet/CheckboxViolet'
import common from '@modals/Timetable/AddLessonOrMeeting/components/common.module.css'
import s from './index.module.css'

export const WEEK_DAYS = [
  { day: 1, label: 'Понедельник', shortLabel: 'ПН', prepositional: 'понедельник' },
  { day: 2, label: 'Вторник', shortLabel: 'ВТ', prepositional: 'вторник' },
  { day: 3, label: 'Среда', shortLabel: 'СР', prepositional: 'среду' },
  { day: 4, label: 'Четверг', shortLabel: 'ЧТ', prepositional: 'четверг' },
  { day: 5, label: 'Пятница', shortLabel: 'ПТ', prepositional: 'пятницу' },
  { day: 6, label: 'Суббота', shortLabel: 'СБ', prepositional: 'субботу' },
  { day: 7, label: 'Воскресенье', shortLabel: 'ВС', prepositional: 'воскресенье' },
]

const DATE_FORMAT = 'YYYY-MM-DD'
const DEFAULT_START_TIME = '12:00'
const NO_END_DATE_YEARS = 5

const getNextDateByWeekDay = (weekDay) => {
  const today = dayjs()
  const todayWeekDay = today.day() || 7
  const diff = (weekDay - todayWeekDay + 7) % 7

  return today.add(diff, 'day').format(DATE_FORMAT)
}

const parseTime = (value) => {
  const [hour = 0, minutes = 0] = String(value || DEFAULT_START_TIME)
    .split(':')
    .map((item) => Number(item))

  return { hour, minutes }
}

const formatTime = ({ hour, minutes }) =>
  `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

const getDefaultSchedule = () => ({
  type: 'daily',
  noEndDate: true,
  startDate: dayjs().format(DATE_FORMAT),
  endDate: dayjs().add(NO_END_DATE_YEARS, 'year').format(DATE_FORMAT),
  value: WEEK_DAYS.map(({ day, label }) => ({
    day,
    label,
    active: false,
    start_time: '',
    hour: 12,
    minutes: 0,
    hourEnd: 13,
    minutesEnd: 0,
    slotSelected: false,
  })),
})

const getDailySchedule = (schedule) => schedule?.value ?? schedule?.daily ?? []

export const getPrivateScheduleSelectedDays = (schedule) =>
  getDailySchedule(schedule)
    .filter((item) => item?.active)
    .map((item) => ({
      ...item,
      prepositional:
        WEEK_DAYS.find(({ day }) => Number(day) === Number(item.day))?.prepositional ??
        item.label?.toLowerCase(),
    }))

export const hasPrivateScheduleDays = (schedule) =>
  getPrivateScheduleSelectedDays(schedule).length > 0

export const hasPrivateScheduleDaysWithoutTime = (schedule) =>
  getPrivateScheduleSelectedDays(schedule).some((item) => !item?.start_time || item.end_time === '')

export const hasPrivateScheduleEqualTimes = (schedule) =>
  getPrivateScheduleSelectedDays(schedule).some((item) =>
    Boolean(item.end_time) && item.end_time === item.start_time,
  )

export const hasPrivateScheduleInvalidDates = (schedule) =>
  hasPrivateScheduleDays(schedule) &&
  (
    !schedule?.startDate ||
    (!schedule?.noEndDate && !schedule?.endDate) ||
    (!schedule?.noEndDate && dayjs(schedule.endDate).isBefore(dayjs(schedule.startDate), 'day'))
  )

export const buildPrivateScheduleCustomDaysValue = (schedule, durationMinutes) => {
  const duration = Number(durationMinutes) || 60

  return {
    type: 'daily',
    value: getDailySchedule(schedule).map(({ end_time, ...item }) => {
      const { hour, minutes } = parseTime(item.start_time)
      const endTime = dayjs()
        .hour(hour)
        .minute(minutes)
        .second(0)
        .millisecond(0)
        .add(duration, 'minute')
      const end = end_time
        ? parseTime(end_time)
        : { hour: endTime.hour(), minutes: endTime.minute() }

      return {
        ...item,
        hour,
        minutes,
        hourEnd: end.hour,
        minutesEnd: end.minutes,
      }
    }),
  }
}

export const buildPrivateScheduleLessonData = ({
  schedule,
  durationMinutes,
  directionId,
  teacherId,
  childrenId,
  linkToMeeting = '',
  linkToMeetingForChildren,
}) => {
  const data = {
    name: '',
    conference_type: 'private',
    interval_type: 'custom-days',
    speaker_id: teacherId ? Number(teacherId) : null,
    seminariansСlasses: teacherId
      ? [{ speaker_id: Number(teacherId), main: true }]
      : [],
    direction_id: Number(directionId),
    ...(childrenId != null && { childrens: [Number(childrenId)] }),
    start_date: schedule?.startDate ?? dayjs().format(DATE_FORMAT),
    end_date: schedule?.noEndDate
      ? dayjs(schedule?.startDate || undefined).add(NO_END_DATE_YEARS, 'year').format(DATE_FORMAT)
      : schedule?.endDate,
    customDaysValue: buildPrivateScheduleCustomDaysValue(schedule, durationMinutes),
    link_to_meeting: linkToMeeting,
    inRecording: 0,
    isOffline: 0,
    isOpen: 0,
    priceViaSchedule: '',
    lessonInBigBlueButton: 0,
    scheduleInGoogleCalendar: false,
  }

  if (linkToMeetingForChildren !== undefined) {
    data.link_to_meeting_for_children = linkToMeetingForChildren
  }

  return data
}

export const getPrivateLessonLinkFromSeminarian = (
  seminarian,
  isSeparateLinksForStudentsAndTeachers = false,
) => {
  if (isSeparateLinksForStudentsAndTeachers) {
    return (
      seminarian?.url_conference_for_children ??
      seminarian?.userProfile?.url_conference_for_children ??
      ''
    )
  }

  return seminarian?.url_conference ?? seminarian?.userProfile?.url_conference ?? ''
}

const FreeTimeCell = ({ day, teacherId, selectedTime, onSelectTime }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const date = useMemo(() => getNextDateByWeekDay(day.day), [day.day])

  useEffect(() => {
    if (!teacherId || !date) return

    setLoading(true)
    timetableBeta
      .getFreeOrBusyWorkingHoursEmployees({
        idsEmployees: JSON.stringify([Number(teacherId)]),
        start: date,
        end: date,
      })
      .then(({ data }) => {
        if (data?.status === 'success') {
          setData(data?.data?.[teacherId] ?? data?.data?.[Number(teacherId)] ?? null)
        }
      })
      .finally(() => setLoading(false))
  }, [date, teacherId])

  const dayData = data?.days?.[0]
  const key = data?.has_work_hours ? 'free' : 'busy'
  const timeList = dayData?.[key] ?? []

  if (!teacherId) {
    return <span className={s.hintEmpty}>Выберите педагога</span>
  }

  if (loading) {
    return <span className={s.hintEmpty}>Загрузка...</span>
  }

  if (!data) {
    return <span className={s.hintEmpty}>Рабочее время не найдено</span>
  }

  if (!timeList.length) {
    return <span className={s.hintEmpty}>Нет свободного времени</span>
  }

  return (
    <div className={s.timeList}>
      {timeList.map((time) => (
        <button
          type="button"
          key={`${time.start_time}-${time.end_time}`}
          className={classNames(
            s.timeChip,
            selectedTime === time.start_time && s.timeChipActive,
          )}
          onClick={() => onSelectTime(day.day, time.start_time)}
        >
          {time.start_time} - {time.end_time}
        </button>
      ))}
      {!data?.has_work_hours && (
        <span className={s.hintMeta}>Показано занятое время</span>
      )}
    </div>
  )
}

const PrivateSchedulePlanner = ({ schedule, onChange, teacherId, durationMinutes }) => {
  const normalizedSchedule = useMemo(() => ({
    ...getDefaultSchedule(),
    ...schedule,
    value: schedule?.value?.length ? schedule.value : getDefaultSchedule().value,
  }), [schedule])

  useEffect(() => {
    if (!schedule?.value?.length) {
      onChange(normalizedSchedule)
    }
  }, [normalizedSchedule, onChange, schedule?.value?.length])

  const updateSchedule = (updater) => {
    const nextSchedule = typeof updater === 'function'
      ? updater(normalizedSchedule)
      : updater

    onChange(nextSchedule)
  }

  const updateDay = (day, patch) => {
    updateSchedule((prev) => ({
      ...prev,
      value: prev.value.map((item) => (
        Number(item.day) === Number(day)
          ? { ...item, ...patch }
          : item
      )),
    }))
  }

  const handleToggleDay = (day) => {
    const current = normalizedSchedule.value.find((item) => Number(item.day) === Number(day))
    updateDay(day, {
      active: !current?.active,
      start_time: current?.start_time ?? '',
    })
  }

  const handleChangeStartTime = (day, value) => {
    updateDay(day, {
      active: true,
      start_time: value,
      end_time: undefined,
      slotSelected: false,
    })
  }

  const handleSelectFreeTime = (day, value) => {
    updateDay(day, {
      active: true,
      start_time: value,
      end_time: undefined,
      slotSelected: true,
    })
  }

  const handleNoEndDateChange = (checked) => {
    updateSchedule((prev) => ({
      ...prev,
      noEndDate: checked,
      endDate: checked
        ? dayjs(prev.startDate || undefined).add(NO_END_DATE_YEARS, 'year').format(DATE_FORMAT)
        : null,
    }))
  }

  const duration = Number(durationMinutes) || 60

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <span>Добавьте занятие в расписание</span>
        <Tooltip title="Вы можете сразу добавить занятия в расписание с выбранным педагогом. Выберите дни недели и время начала каждого занятия. Время окончания рассчитывается по стандартной длительности и доступно для ручного изменения. При изменении времени начала окончание рассчитывается заново. Эти занятия создадутся без даты окончания">
          <InfoCircleOutlined className={s.infoIcon} />
        </Tooltip>
      </div>

      <div className={s.scheduleTable}>
        <div className={classNames(s.tableRow, s.tableHead)}>
          <div>День недели</div>
          <div>Время начала</div>
          <div>Время окончания</div>
          <div>Свободное время</div>
        </div>
        {normalizedSchedule.value.map((day) => {
          const start = day.start_time ? parseTime(day.start_time) : null
          const end = start
            ? dayjs().hour(start.hour).minute(start.minutes).add(duration, 'minute')
            : null

          return (
            <div key={day.day} className={s.tableRow}>
              <div className={s.dayCell}>
                <CheckboxViolet
                  value={day.active}
                  text={WEEK_DAYS.find((item) => item.day === day.day)?.shortLabel ?? day.label}
                  onClick={() => handleToggleDay(day.day)}
                  blockClassName={s.checkboxBlock}
                />
              </div>
              <div className={s.timeInputCell}>
                {day.active ? (
                  <input
                    type="time"
                    aria-label={`Время начала, ${day.label}`}
                    className={s.timeInput}
                    value={day.start_time}
                    onChange={(event) => handleChangeStartTime(day.day, event.target.value)}
                  />
                ) : (
                  <span className={s.hintEmpty}>-</span>
                )}
              </div>
              <div className={s.timeInputCell}>
                {day.active ? (
                  <input
                    type="time"
                    aria-label={`Время окончания, ${day.label}`}
                    className={s.timeInput}
                    value={day.end_time ?? (end ? formatTime({ hour: end.hour(), minutes: end.minute() }) : '')}
                    onChange={(event) => updateDay(day.day, { end_time: event.target.value })}
                  />
                ) : (
                  <span className={s.hintEmpty}>-</span>
                )}
              </div>
              <div className={s.freeTimeCell}>
                {day.active ? (
                  <FreeTimeCell
                    day={day}
                    teacherId={teacherId}
                    selectedTime={day.start_time}
                    onSelectTime={handleSelectFreeTime}
                  />
                ) : (
                  <span className={s.hintEmpty}>Выберите день</span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {hasPrivateScheduleDays(normalizedSchedule) && (
        <div className={s.dateSettings}>
          <div className={s.noEndDateRow}>
            <Switch
              checked={normalizedSchedule.noEndDate}
              onChange={handleNoEndDateChange}
            />
            <span>Нет даты окончания</span>
          </div>
          <div className={s.dateRow}>
            <div className={s.dateField}>
              <span>Дата начала</span>
              <DatePicker
                value={normalizedSchedule.startDate ? dayjs(normalizedSchedule.startDate) : null}
                onChange={(value) => {
                  const startDate = value ? value.format(DATE_FORMAT) : null
                  updateSchedule((prev) => ({
                    ...prev,
                    startDate,
                    endDate: prev.noEndDate && startDate
                      ? dayjs(startDate).add(NO_END_DATE_YEARS, 'year').format(DATE_FORMAT)
                      : prev.endDate,
                  }))
                }}
                className={classNames(common.antd, s.datePicker)}
                placeholder="Выберите дату начала"
                allowClear={false}
              />
            </div>
            {!normalizedSchedule.noEndDate && (
              <div className={s.dateField}>
                <span>Дата окончания</span>
                <DatePicker
                  value={normalizedSchedule.endDate ? dayjs(normalizedSchedule.endDate) : null}
                  onChange={(value) => {
                    updateSchedule((prev) => ({
                      ...prev,
                      endDate: value ? value.format(DATE_FORMAT) : null,
                    }))
                  }}
                  className={classNames(common.antd, s.datePicker)}
                  placeholder="Выберите дату окончания"
                  allowClear={false}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PrivateSchedulePlanner
